import React from 'react';

const Bone = ({ w, h = 14, r = 4, style }) => (
  <div className="skel-bone" style={{ width: w, height: h, borderRadius: r, ...style }} />
);

export function MatchRowSkeleton() {
  return (
    <div className="skel-match-row">
      <div className="skel-match-status">
        <Bone w={32} h={14} />
        <Bone w={40} h={10} />
      </div>
      <div className="skel-match-body">
        <Bone w="60%" h={13} />
        <Bone w="55%" h={13} style={{ marginTop: 8 }} />
      </div>
      <div className="skel-match-scores">
        <Bone w={24} h={24} r={4} />
        <Bone w={24} h={24} r={4} />
      </div>
    </div>
  );
}

export function MatchGroupSkeleton({ rows = 3 }) {
  return (
    <div className="skel-group">
      <div className="skel-group-head">
        <Bone w={140} h={14} />
        <Bone w={80} h={12} />
      </div>
      {Array.from({ length: rows }).map((_, i) => <MatchRowSkeleton key={i} />)}
    </div>
  );
}

export function CardSkeleton({ count = 3 }) {
  return (
    <div className="grid-cards">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="skel-card">
          <Bone w="70%" h={12} />
          <Bone w="90%" h={16} style={{ marginTop: 10 }} />
          <Bone w="50%" h={12} style={{ marginTop: 8 }} />
          <Bone w="100%" h={4} r={2} style={{ marginTop: 12 }} />
        </div>
      ))}
    </div>
  );
}

export function StatsSkeleton() {
  return (
    <div className="home-quick-stats">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="skel-stat">
          <Bone w={36} h={22} r={6} />
          <Bone w={48} h={10} style={{ marginTop: 4 }} />
        </div>
      ))}
    </div>
  );
}

// Home page skeleton
export function HomeSkeleton() {
  const gb = 'rgba(255,255,255,0.08)';
  const gl = 'rgba(255,255,255,0.05)';
  return (
    <div className="container" style={{ animation: 'fadeIn 0.3s ease' }}>
      {/* Hero */}
      <div className="home-hero" style={{ position: 'relative', overflow: 'hidden' }}>
        <div className="home-hero-content home-hero-layout">
          <div className="home-hero-left">
            <Bone w="70%" h={26} r={6} style={{ background: gb }} />
            <Bone w="90%" h={14} r={4} style={{ marginTop: 8, background: gl }} />
            <Bone w="60%" h={12} r={3} style={{ marginTop: 6, background: gl }} />
            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <Bone w={150} h={38} r={8} style={{ background: 'rgba(189,255,0,0.12)' }} />
              <Bone w={150} h={38} r={8} style={{ background: gb }} />
            </div>
          </div>
          <div className="home-hero-right">
            <div className="hero-stat-grid">
              {[0,1,2,3].map(i => (
                <div key={i} className="hero-stat-item">
                  <Bone w={32} h={22} r={4} style={{ background: 'rgba(189,255,0,0.1)', margin: '0 auto' }} />
                  <Bone w={50} h={10} r={3} style={{ marginTop: 4, background: gl, margin: '4px auto 0' }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats - mobile */}
      <section className="home-quick-stats home-quick-stats-mobile">
        <div className="home-stat-strip">
          {[0,1,2,3].map(i => (
            <React.Fragment key={i}>
              {i > 0 && <div className="home-stat-div" />}
              <div className="home-stat-chip">
                <Bone w={24} h={18} r={4} style={{ background: gb }} />
                <Bone w={36} h={9} r={3} style={{ background: gl }} />
              </div>
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* Live Scores Section */}
      <section style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <Bone w={120} h={14} r={4} />
          <Bone w={70} h={12} r={3} />
        </div>
        {/* Tournament filter pills */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 6, overflowX: 'auto' }}>
          <Bone w={40} h={28} r={20} />
          <Bone w={160} h={28} r={20} />
          <Bone w={140} h={28} r={20} />
        </div>
        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--gray-100)', marginBottom: 12 }}>
          {['Live', 'Upcoming', 'Results'].map((l, i) => (
            <div key={i} style={{ padding: '10px 16px', borderBottom: i === 1 ? '2px solid var(--navy)' : '2px solid transparent', marginBottom: -2 }}>
              <Bone w={l.length * 7} h={12} r={3} style={i === 1 ? { background: 'rgba(0,32,91,0.15)' } : {}} />
            </div>
          ))}
        </div>
        {/* Match cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <Bone w={140} h={11} r={3} />
              <div style={{ flex: 1, height: 1, background: 'var(--gray-100)' }} />
              <Bone w={80} h={10} r={3} />
            </div>
            {[0, 1, 2].map(i => (
              <div key={i} style={{ background: 'var(--card-bg)', borderRadius: 10, border: '1px solid var(--card-border)', overflow: 'hidden', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'stretch' }}>
                  <div style={{ width: 64, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, padding: '10px 0', borderRight: '1px solid var(--card-border)' }}>
                    <Bone w={32} h={12} r={3} />
                    <Bone w={40} h={9} r={3} />
                  </div>
                  <div style={{ flex: 1, padding: '8px 14px' }}>
                    <Bone w="60%" h={13} r={3} style={{ marginBottom: 6 }} />
                    <Bone w="55%" h={13} r={3} />
                  </div>
                  <div style={{ display: 'flex', gap: 2, padding: '8px 14px', alignItems: 'center' }}>
                    <Bone w={28} h={26} r={4} />
                    <Bone w={28} h={26} r={4} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Tournaments */}
      <section style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <Bone w={200} h={14} r={4} />
          <Bone w={70} h={12} r={3} />
        </div>
        <div className="grid-cards">
          {[0, 1, 2].map(i => <TournamentCardSkeleton key={i} />)}
        </div>
      </section>

      {/* Top Players - desktop */}
      <section className="home-desktop-only" style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <Bone w={180} h={14} r={4} />
          <Bone w={70} h={12} r={3} />
        </div>
        <div className="grid-players-home">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="p-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <Bone w={34} h={34} r={10} />
                <div style={{ flex: 1 }}>
                  <Bone w="65%" h={14} r={4} />
                  <Bone w="35%" h={10} r={3} style={{ marginTop: 4 }} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                <Bone w={28} h={12} r={3} /><Bone w={28} h={12} r={3} /><Bone w={28} h={12} r={3} />
              </div>
              <Bone w="100%" h={3} r={2} />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// Reusable tournament card skeleton
function TournamentCardSkeleton() {
  const surfaceColor = 'var(--blue)';
  return (
    <div className="t-card" style={{ border: `1.5px solid ${surfaceColor}`, boxShadow: '0 2px 6px rgba(0,0,0,0.08)', borderRadius: '12px', overflow: 'hidden', background: 'var(--card-bg)' }}>
      <div className="t-header" style={{ background: `linear-gradient(135deg, ${surfaceColor}18 0%, ${surfaceColor}08 100%)` }}>
        <div className="t-header-top">
          <Bone w={60} h={18} r={10} style={{ background: 'rgba(0,0,0,0.08)' }} />
          <Bone w={70} h={16} r={4} style={{ background: 'rgba(0,0,0,0.06)' }} />
        </div>
        <Bone w="75%" h={17} r={4} style={{ marginTop: 4, background: 'rgba(0,0,0,0.1)' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 6 }}>
          <Bone w={12} h={12} r={6} style={{ background: 'rgba(0,0,0,0.06)' }} />
          <Bone w={80} h={12} r={3} style={{ background: 'rgba(0,0,0,0.06)' }} />
        </div>
      </div>
      <div className="t-body">
        <div className="t-stats">
          <div className="t-stat-item"><Bone w={50} h={13} r={3} style={{ margin: '0 auto' }} /><Bone w={40} h={9} r={2} style={{ margin: '4px auto 0' }} /></div>
          <div className="t-stat-div" />
          <div className="t-stat-item"><Bone w={90} h={13} r={3} style={{ margin: '0 auto' }} /><Bone w={45} h={9} r={2} style={{ margin: '4px auto 0' }} /></div>
          <div className="t-stat-div" />
          <div className="t-stat-item"><Bone w={35} h={13} r={3} style={{ margin: '0 auto' }} /><Bone w={40} h={9} r={2} style={{ margin: '4px auto 0' }} /></div>
        </div>
        <div className="t-bottom">
          <div className="t-bar-wrap">
            <Bone w="100%" h={4} r={2} />
            <Bone w={50} h={10} r={3} style={{ marginTop: 3 }} />
          </div>
          <Bone w={50} h={22} r={6} />
        </div>
      </div>
    </div>
  );
}

// Players list skeleton
export function PlayersListSkeleton() {
  return (
    <div className="container-sm" style={{ animation: 'fadeIn 0.3s ease' }}>
      <Bone w={100} h={16} r={4} style={{ marginBottom: 16 }} />
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <Bone w="100%" h={38} r={8} />
        <Bone w={120} h={38} r={8} />
      </div>
      <div className="grid-players">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="p-card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <Bone w={34} h={34} r={10} />
              <div style={{ flex: 1 }}>
                <Bone w="70%" h={14} r={4} />
                <Bone w="40%" h={10} r={3} style={{ marginTop: 4 }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              <Bone w={30} h={12} r={3} />
              <Bone w={30} h={12} r={3} />
              <Bone w={30} h={12} r={3} />
            </div>
            <Bone w="100%" h={3} r={2} />
          </div>
        ))}
      </div>
    </div>
  );
}

// Player detail skeleton
export function PlayerDetailSkeleton() {
  return (
    <div className="container-sm" style={{ animation: 'fadeIn 0.3s ease' }}>
      {/* Back button */}
      <Bone w={60} h={14} r={4} style={{ marginBottom: 14 }} />
      
      {/* Player header card */}
      <div className="skel-card" style={{ padding: 32, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
          <Bone w={48} h={48} r={12} />
          <div style={{ flex: 1 }}>
            <Bone w={160} h={22} r={4} />
            <Bone w={60} h={12} r={3} style={{ marginTop: 6 }} />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
          <Bone w={80} h={11} r={3} />
          <Bone w={80} h={13} r={3} />
        </div>
        <Bone w="100%" h={8} r={4} />
        <div style={{ display: 'flex', gap: 16, marginTop: 10 }}>
          <Bone w={50} h={12} r={3} />
          <Bone w={50} h={12} r={3} />
        </div>
      </div>
      
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Bone w={90} h={30} r={20} />
        <Bone w={100} h={30} r={20} />
      </div>
      
      {/* Loading State */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', minHeight: 200 }}>
        {/* Animated loading line */}
        <div style={{ width: '100%', maxWidth: 400, height: 4, background: '#EEF1F6', borderRadius: 2, overflow: 'hidden', marginBottom: 16 }}>
          <div style={{ width: '40%', height: '100%', background: 'linear-gradient(90deg, #00205B, #1E88E5)', borderRadius: 2, animation: 'slideRight 1.5s ease-in-out infinite' }} />
        </div>
        <Bone w={160} h={14} r={4} style={{ marginBottom: 8 }} />
        <Bone w={220} h={12} r={3} />
      </div>
    </div>
  );
}

// Tournament list skeleton
export function TournamentListSkeleton() {
  return (
    <div className="container" style={{ animation: 'fadeIn 0.3s ease' }}>
      <Bone w={160} h={16} r={4} style={{ marginBottom: 16 }} />
      
      {/* Search Input Skeleton */}
      <div style={{ marginBottom: 16, position: 'relative' }}>
        <Bone w="100%" h={40} r={8} />
      </div>
      
      <div className="filter-tabs" style={{ marginBottom: 16 }}>
        {['All', 'Upcoming', 'Ongoing', 'Completed'].map((l, i) => (
          <div key={i} style={{ padding: '8px 16px', borderRadius: 6, border: '1.5px solid var(--gray-200)', background: i === 0 ? 'var(--navy)' : 'var(--card-bg)' }}>
            <Bone w={l.length * 7} h={12} r={3} style={i === 0 ? { background: 'rgba(189,255,0,0.2)' } : {}} />
          </div>
        ))}
      </div>
      <div className="grid-cards">
        {[0, 1, 2].map(i => <TournamentCardSkeleton key={i} />)}
      </div>
    </div>
  );
}

// Schedule skeleton
export function ScheduleSkeleton() {
  return (
    <div className="container-sm" style={{ animation: 'fadeIn 0.3s ease' }}>
      {[0, 1].map(g => (
        <div key={g} style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <Bone w={40} h={40} r={10} />
            <div>
              <Bone w={100} h={14} r={4} />
              <Bone w={80} h={10} r={3} style={{ marginTop: 4 }} />
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingLeft: 18, borderLeft: '2px solid #EEF1F6' }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="skel-card" style={{ padding: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                  <Bone w="50%" h={11} r={3} />
                  <Bone w={60} h={18} r={4} />
                </div>
                <Bone w="70%" h={13} r={3} />
                <Bone w="60%" h={13} r={3} style={{ marginTop: 6 }} />
                <div style={{ display: 'flex', gap: 8, marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--gray-100)' }}>
                  <Bone w={50} h={20} r={6} />
                  <Bone w={60} h={20} r={4} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ManageTournament skeleton
export function ManageTournamentSkeleton() {
  return (
    <div className="container" style={{ animation: 'fadeIn 0.3s ease' }}>
      {/* Back button */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <Bone w={60} h={14} r={4} />
        <div style={{ display: 'flex', gap: 6 }}>
          <Bone w={80} h={18} r={10} />
          <Bone w={60} h={18} r={10} />
        </div>
      </div>
      
      {/* Tournament title and info */}
      <div style={{ marginBottom: 16 }}>
        <Bone w="70%" h={24} r={4} style={{ marginBottom: 6 }} />
        <div style={{ display: 'flex', gap: 12 }}>
          <Bone w={100} h={11} r={3} />
          <Bone w={80} h={11} r={3} />
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--gray-100)', marginBottom: 16, overflowX: 'auto' }}>
        {['Draw', 'Scores', 'Players & Fees', 'Info'].map((l, i) => (
          <div key={i} style={{ padding: '10px 16px', borderBottom: i === 0 ? '2px solid var(--navy)' : '2px solid transparent', marginBottom: -2, flexShrink: 0 }}>
            <Bone w={l.length * 6.5} h={12} r={3} style={i === 0 ? { background: 'rgba(189,255,0,0.2)' } : {}} />
          </div>
        ))}
      </div>
      
      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, padding: '12px 16px', background: 'var(--card-bg)', borderRadius: 10, border: '1px solid var(--card-border)' }}>
        <Bone w={180} h={14} r={4} />
        <div style={{ display: 'flex', gap: 6 }}>
          <Bone w={36} h={36} r={8} />
          <Bone w={36} h={36} r={8} />
          <Bone w={36} h={36} r={8} />
          <Bone w={36} h={36} r={8} />
          <Bone w={36} h={36} r={8} />
        </div>
      </div>
      
      {/* Search bar */}
      <div style={{ marginBottom: 20, position: 'relative' }}>
        <Bone w="100%" h={44} r={8} />
      </div>
      
      {/* Loading State */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', minHeight: 300 }}>
        {/* Animated loading line */}
        <div style={{ width: '100%', maxWidth: 400, height: 4, background: '#EEF1F6', borderRadius: 2, overflow: 'hidden', marginBottom: 16 }}>
          <div style={{ width: '40%', height: '100%', background: 'linear-gradient(90deg, #00205B, #1E88E5)', borderRadius: 2, animation: 'slideRight 1.5s ease-in-out infinite' }} />
        </div>
        <Bone w={200} h={14} r={4} style={{ marginBottom: 8 }} />
        <Bone w={280} h={12} r={3} />
      </div>
    </div>
  );
}

// Tournament detail skeleton
export function TournamentDetailSkeleton() {
  return (
    <div className="container" style={{ animation: 'fadeIn 0.3s ease' }}>
      {/* Back button */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <Bone w={60} h={14} r={4} />
        <div style={{ display: 'flex', gap: 6 }}>
          <Bone w={80} h={18} r={10} />
          <Bone w={30} h={30} r={6} />
          <Bone w={30} h={30} r={6} />
        </div>
      </div>
      
      {/* Tournament title and info */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Bone w="70%" h={18} r={4} style={{ marginBottom: 6 }} />
          <div style={{ display: 'flex', gap: 12 }}>
            <Bone w={100} h={11} r={3} />
            <Bone w={80} h={11} r={3} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <Bone w={140} h={36} r={8} />
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--gray-100)', marginBottom: 16, overflowX: 'auto' }}>
        {['Draw', 'Results', 'Players', 'Discussion', 'Info'].map((l, i) => (
          <div key={i} style={{ padding: '10px 16px', borderBottom: i === 0 ? '2px solid var(--navy)' : '2px solid transparent', marginBottom: -2, flexShrink: 0 }}>
            <Bone w={l.length * 7} h={12} r={3} style={i === 0 ? { background: 'rgba(0,32,91,0.15)' } : {}} />
          </div>
        ))}
      </div>

      {/* Loading State */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', minHeight: 300 }}>
        {/* Animated loading line */}
        <div style={{ width: '100%', maxWidth: 400, height: 4, background: '#EEF1F6', borderRadius: 2, overflow: 'hidden', marginBottom: 16 }}>
          <div style={{ width: '40%', height: '100%', background: 'linear-gradient(90deg, #00205B, #1E88E5)', borderRadius: 2, animation: 'slideRight 1.5s ease-in-out infinite' }} />
        </div>
        <Bone w={180} h={14} r={4} style={{ marginBottom: 8 }} />
        <Bone w={250} h={12} r={3} />
      </div>
    </div>
  );
}

// Match detail skeleton
export function MatchDetailSkeleton() {
  const gb = 'rgba(255,255,255,0.08)';
  const gl = 'rgba(255,255,255,0.05)';
  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '8px 12px 32px', animation: 'fadeIn 0.3s ease' }}>
      <Bone w={60} h={14} r={4} style={{ marginBottom: 14 }} />
      <div style={{ borderRadius: 16, overflow: 'hidden', border: '1px solid var(--card-border)', background: 'var(--card-bg)', marginBottom: 14 }}>
        <div style={{ background: 'linear-gradient(135deg, #00205B 60%, #1E88E5)', padding: '16px 18px 14px' }}>
          <Bone w={120} h={11} r={3} style={{ background: gl, marginBottom: 6 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Bone w={80} h={12} r={3} style={{ background: gb }} />
            <Bone w={50} h={16} r={4} style={{ background: gb }} />
          </div>
        </div>
        <div style={{ padding: '18px 16px 14px' }}>
          {[0, 1].map(i => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 8px', borderBottom: i === 0 ? '1px solid var(--gray-100)' : 'none' }}>
              <Bone w={36} h={36} r={18} />
              <div style={{ flex: 1 }}>
                <Bone w="60%" h={14} r={4} />
                <Bone w="35%" h={10} r={3} style={{ marginTop: 4 }} />
              </div>
              <div style={{ display: 'flex', gap: 3 }}>
                <Bone w={26} h={26} r={5} />
                <Bone w={26} h={26} r={5} />
                <Bone w={26} h={26} r={5} />
              </div>
            </div>
          ))}
        </div>
        <div style={{ padding: '0 16px 14px', display: 'flex', gap: 6 }}>
          <Bone w={80} h={22} r={4} />
          <Bone w={55} h={22} r={4} />
          <Bone w={65} h={22} r={4} />
        </div>
      </div>
      <Bone w={90} h={12} r={3} style={{ marginBottom: 10 }} />
      <div className="skel-card" style={{ padding: 14 }}>
        <Bone w="100%" h={38} r={8} style={{ marginBottom: 12 }} />
        <Bone w="80%" h={13} r={3} />
        <Bone w="60%" h={13} r={3} style={{ marginTop: 8 }} />
      </div>
    </div>
  );
}

// Organizer dashboard skeleton
export function OrganizerDashboardSkeleton() {
  return (
    <div className="container" style={{ animation: 'fadeIn 0.3s ease' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Bone w={180} h={20} r={4} />
          <Bone w={260} h={12} r={3} style={{ marginTop: 6 }} />
        </div>
        <Bone w={160} h={40} r={8} />
      </div>
      
      {/* Stats Cards */}
      <div className="org-stats" style={{ marginBottom: 24 }}>
        {[0, 1, 2].map(i => (
          <div key={i} className="org-stat-card">
            <Bone w={36} h={36} r={10} />
            <div>
              <Bone w={32} h={22} r={4} />
              <Bone w={60} h={10} r={3} style={{ marginTop: 4 }} />
            </div>
          </div>
        ))}
      </div>
      
      {/* Loading State */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', minHeight: 300 }}>
        {/* Animated loading line */}
        <div style={{ width: '100%', maxWidth: 400, height: 4, background: '#EEF1F6', borderRadius: 2, overflow: 'hidden', marginBottom: 16 }}>
          <div style={{ width: '40%', height: '100%', background: 'linear-gradient(90deg, #00205B, #1E88E5)', borderRadius: 2, animation: 'slideRight 1.5s ease-in-out infinite' }} />
        </div>
        <Bone w={200} h={14} r={4} style={{ marginBottom: 8 }} />
        <Bone w={280} h={12} r={3} />
      </div>
    </div>
  );
}

// Profile skeleton
export function ProfileSkeleton() {
  return (
    <div className="container-sm" style={{ padding: '0 16px 24px', animation: 'fadeIn 0.3s ease' }}>
      <div style={{ background: 'linear-gradient(135deg, #00205B 60%, #0A2E6E)', borderRadius: '0 0 20px 20px', padding: '28px 24px', marginLeft: -16, marginRight: -16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Bone w={52} h={52} r={14} style={{ background: 'rgba(255,255,255,0.1)' }} />
          <div style={{ flex: 1 }}>
            <Bone w={140} h={22} r={4} style={{ background: 'rgba(255,255,255,0.12)' }} />
            <Bone w={60} h={12} r={3} style={{ marginTop: 6, background: 'rgba(255,255,255,0.06)' }} />
          </div>
          <Bone w={50} h={30} r={8} style={{ background: 'rgba(255,255,255,0.08)' }} />
        </div>
      </div>
      <div style={{ background: 'var(--card-bg)', borderRadius: 14, marginTop: -20, marginLeft: 16, marginRight: 16, padding: '16px 20px', border: '1px solid var(--card-border)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
          <Bone w={80} h={11} r={3} />
          <Bone w={80} h={13} r={3} />
        </div>
        <Bone w="100%" h={8} r={4} />
        <div style={{ display: 'flex', gap: 16, marginTop: 10 }}>
          <Bone w={50} h={12} r={3} />
          <Bone w={50} h={12} r={3} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 28, marginBottom: 12 }}>
        <Bone w={90} h={30} r={20} />
        <Bone w={100} h={30} r={20} />
      </div>
      <div className="mh-grid">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="skel-card" style={{ padding: 14 }}>
            <Bone w="60%" h={11} r={3} style={{ marginBottom: 10 }} />
            <Bone w="80%" h={13} r={3} />
            <Bone w="70%" h={13} r={3} style={{ marginTop: 6 }} />
          </div>
        ))}
      </div>
    </div>
  );
}
